<template>
    <div :class="theme.datePicker">
        <div :class="theme.header">
            <div>
                <slot name="label">
                    <v-text type="subheadling" white>
                        {{ label }}
                    </v-text>
                </slot>
            </div>
            <div
                @click="handleChangeMode('year')"
                :class="classHeaderYear"
            >
                <slot name="current-year" :date="valueMoment">
                    <v-text type="subheadling" medium white>
                        {{ valueMoment.format('YYYY') }}
                    </v-text>
                </slot>
            </div>

            <div :class="theme.headerTitleWrap">
                <transition
                    name="slideTitle"
                    :enter-active-class="theme.slideTitleEnterActive"
                    :enter-class="theme.slideTitleEnter"
                    :enter-to-class="theme.slideTitleEnterTo"
                    :leave-active-class="theme.slideTitleLeaveActive"
                    :leave-class="theme.slideTitleLeave"
                    :leave-to-class="theme.slideTitleLeaveTo"
                >
                    <div
                        @click="handleChangeMode('day')"
                        :key="valueMoment.format('MMMM, ddd DD')"
                        :class="classHeaderDay"
                    >
                        <slot name="current-year" :date="valueMoment">
                            <v-text headline medium white>
                                {{ valueMoment.format('MMM DD') }}
                            </v-text>
                        </slot>
                    </div>
                </transition>
            </div>
        </div>

        <v-animation-height>

            <div v-if="isModeDay" :class="theme.calendar">
                <div :class="theme.calendarNavButtons">
                    <v-icon-button
                        :class="theme.calendarNavButton"
                        @click="handlePrevMonth"
                    >
                        keyboard_arrow_left
                    </v-icon-button>

                    <v-icon-button
                        :class="theme.calendarNavButton"
                        @click="handleNextMonth"
                    >
                        keyboard_arrow_right
                    </v-icon-button>
                </div>

                <transition
                    name="slide"
                    :enter-active-class="slide === 'left' ? theme.slideLeftEnterActive : theme.slideRightEnterActive"
                    :enter-class="slide === 'left' ? theme.slideLeftEnter : theme.slideRightEnter"
                    :enter-to-class="slide === 'left' ? theme.slideLeftEnterTo : theme.slideRightEnterTo"
                    :leave-active-class="slide === 'left' ? theme.slideLeftLeaveActive : theme.slideRightLeaveActive"
                    :leave-class="slide === 'left' ? theme.slideLeftLeave : theme.slideRightLeave"
                    :leave-to-class="slide === 'left' ? theme.slideLeftLeaveTo : theme.slideRightLeaveTo"
                    @after-leave="handleAfterLeaveMonth"
                >
                    <table :class="theme.calendarTable" :key="currentMoment.format('YYYY-MM')">
                        <tr>
                            <td :class="[theme.calendarTableCell, theme.isMonthTitle]" colspan="7">
                                {{ currentMoment.format('MMMM YYYY') }}
                            </td>
                        </tr>
                        <tr>
                            <td
                                v-for="weekDay in [0,1,2,3,4,5,6]"
                                :class="[theme.calendarTableCell, theme.isWeekTitle]"
                            >
                                {{ currentMoment.weekday(weekDay).format('dd') }}
                            </td>
                        </tr>

                        <tr
                            v-for="week, index in calendarMatrix"
                            :key="index"
                        >
                            <td
                                v-for="day, index in week"
                                v-if="day.isCurrentMonth"
                                @click="handleSelectDay(day.date)"
                                :key="index"
                                :class="getClassDay(day)"
                                v-ripple.white
                            >
                                {{ day.label }}
                            </td>
                            <td v-else></td>
                        </tr>
                    </table>
                </transition>
            </div>

            <div v-if="isModeDay">
                <div :class="theme.actions">
                    <slot></slot>
                </div>
            </div>

            <div v-if="isModeYear" :class="theme.years" ref="years">
            <div
                v-for="year, index in allYears"
                :key="index"
                :ref="'y' + (year + index)"
                :class="getClassYear(year + index)"
                @click="handleChangeYear(year + index)"
            >
                {{ year + index }}
            </div>
        </div>

        </v-animation-height>
    </div>
</template>

<script>
    import moment from 'moment';
    import { Ripple } from '../../directives';
    import { vFontIcon } from '../FontIcon';
    import { vAnimationHeight } from '../AnimationHeight';
    import { vButton, vIconButton } from '../Button';
    import theme from './datePickerRaw.scss';

    moment.locale('ru');

    function getCalendarMatrix(date, activeDay = undefined) {
        const currentDate = moment(date);
        const format = 'YYYY-MM-DD';
        const CURRENT_MONTH = currentDate.month();
        const TODAY = moment().format(format);
        const ACTIVE_DAY = activeDay;

        const week = (new Array(7)).fill(0);
        const calendar = [];

        currentDate.date(1).isoWeekday(1).add(-1,'day');

        (new Array(6)).fill(0).map(() => {
            const newWeek = week.map(() => {
                const DATE = currentDate.add(1,'day').format(format);
                return {
                    isCurrentMonth: currentDate.month() === CURRENT_MONTH,
                    isToday: DATE === TODAY,
                    isActive: DATE === ACTIVE_DAY,
                    label: currentDate.format('DD'),
                    date: DATE,
                };
            });
            calendar.push(newWeek);
        });

        return calendar;
    }

    function getMoment(date = undefined, format = undefined) {
        return moment(date || undefined, date && format);
    }

    export default {
        name: 'date-picker-raw',
        props: {
            value: String,
            label: String,
            format: {
                type: String,
                default: 'YYYY-MM-DD'
            },
            theme: {
                type: Object,
                default() {
                    return theme;
                }
            }
        },
        directives: {
            Ripple,
        },
        components: {
            vButton,
            vIconButton,
            vFontIcon,
            vAnimationHeight
        },
        updated() {
            this.scrollToCurrentYear();
        },
        data() {
            const currentDate = getMoment(this.value, this.format);

            return {
                slide: 'left', // left | right
                mode: 'day', // day | year
                currentDate: currentDate.isValid()
                    ? currentDate.format('YYYY-MM-DD')
                    : moment().format('YYYY-MM-DD')
            }
        },
        watch: {
            value(newValue) {
                const currentDate = getMoment(newValue, this.format);

                this.currentDate = currentDate.isValid()
                    ? currentDate
                    : moment();
            }
        },
        methods: {
            moment,
            getClassDay(day) {
                return {
                    [this.theme.calendarTableCell]: true,
                    [this.theme.isToday]: day.isToday && !day.isActive,
                    [this.theme.isActive]: day.isActive,
                };
            },
            getClassYear(year) {
                return {
                    [this.theme.year]: true,
                    [this.theme.isActive]: this.currentYear === year,
                }
            },
            changeMonth(step) {
                this.currentDate = moment(this.currentDate).add(step, 'month').format('YYYY-MM-DD');
            },
            handleSelectDay(date) {
                this.currentDate = date;

                this.$emit('input', moment(date).format(this.format));
            },
            handlePrevMonth() {
                this.slide = 'right';
                this.changeMonth(-1);
            },
            handleNextMonth() {
                this.slide = 'left';
                this.changeMonth(1);
            },
            handleChangeMode(mode) {
                this.mode = mode;
            },
            handleChangeYear(year) {
                this.currentDate = this.currentMoment.clone().year(year).format('YYYY-MM-DD');
                this.mode = 'day';

                this.$emit('input', this.valueMoment.clone().year(year).format(this.format));
            },
            scrollToCurrentYear() {
                const $yearsEl = this.$refs.years;

                if (this.mode === 'year' && $yearsEl !== undefined) {
                    const currentYearEls = this.$refs['y' + this.currentYear];
                    if (currentYearEls.length > 0) {
                        const clientRect = currentYearEls[0].getClientRects()[0];
                        $yearsEl.scrollTop = clientRect.top - (36 * 3) - $yearsEl.getClientRects()[0].top;
                    }
                }
            },
            handleAfterLeaveMonth() {
                const event = document.createEvent('Event');
                event.initEvent('resize', true, true);
                document.dispatchEvent(event);
            }
        },
        computed: {
            classHeaderYear() {
                return {
                    [this.theme.headerYear]: true,
                    [this.theme.headerIsInactive]: this.mode !== 'year',
                }
            },
            classHeaderDay() {
                return {
                    [this.theme.headerDay]: true,
                    [this.theme.headerIsInactive]: this.mode !== 'day',
                }
            },
            calendarMatrix() {
                return getCalendarMatrix(
                    this.currentDate,
                    this.value && this.valueMoment.format('YYYY-MM-DD')
                );
            },
            valueMoment() {
                const valueMoment = getMoment(this.value, this.format);
                return valueMoment.isValid()
                    ? valueMoment
                    : moment();
            },
            currentMoment() {
                return moment(this.currentDate);
            },
            currentYear() {
                return this.currentMoment.year();
            },
            isModeDay() {
                return this.mode === 'day';
            },
            isModeYear() {
                return this.mode === 'year';
            },
            allYears() {
                return (new Array(120)).fill(1920);
            }
        }
    }
</script>


// WEBPACK FOOTER //
// DatePickerRaw.vue?74137dc4